import { ListItem, List, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { motion, AnimatePresence } from 'framer-motion';

import THEME from '../../constants/theme';
import { pdfFiles } from '../../utils';

const NavigationMenuItem = ({
  item,
  expandedMenu,
  handleMenuToggle,
  handleScroll,
  location,
  activeSection,
  textColor,
  activeColor
}) => {
  const isHomePage = location.pathname === '/';
  const isParentActive =
    location.pathname === item.path ||
    (item.path !== '/' && location.pathname.startsWith(`${item.path}/`));

  if (item.hasDropdown) {
    return (
      <div>
        <ListItem
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '3px 8px',
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT,
            fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
            textAlign: 'left',
            color: isParentActive ? activeColor : textColor,
            '&:hover': {
              color: activeColor,
              cursor: 'pointer'
            },
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
          }}
        >
          {item.submenu && item.submenu.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginRight: '6px'
              }}
              onClick={e => {
                e.stopPropagation();
                handleMenuToggle(item.title);
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transform:
                    expandedMenu === item.title
                      ? 'rotate(0deg)'
                      : 'rotate(-90deg)',
                  transition: `transform ${THEME.TRANSITIONS.MEDIUM} ease`,
                  color: 'inherit',
                  fontSize: '1.1rem'
                }}
              />
            </Box>
          )}{' '}
          <Typography
            onClick={() => handleScroll('hero', item.path)}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              fontSize: 'inherit',
              cursor: 'pointer',
              fontWeight: 'inherit',
              fontFamily: 'inherit'
            }}
          >
            {item.title}
          </Typography>
        </ListItem>
        <AnimatePresence>
          {item.submenu &&
            item.submenu.length > 0 &&
            expandedMenu === item.title && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
                transition={{ duration: 0.25 }}
              >
                <List sx={{ padding: '0px 0px 0px 20px', margin: 0 }}>
                  {item.submenu.map((submenu, subIndex) => {
                    // Check if this specific submenu item is active
                    let isSubmenuActive = false;
                    if (submenu.id) {
                      // Section on same page - check active section
                      isSubmenuActive = activeSection === submenu.id;
                    } else if (submenu.path) {
                      const pathParts = submenu.path.split('#');
                      const basePath = pathParts[0];
                      const sectionId =
                        pathParts.length > 1 ? pathParts[1] : null;

                      if (sectionId) {
                        // Section on another page - check both path and section
                        isSubmenuActive =
                          location.pathname === basePath &&
                          activeSection === sectionId;
                      } else {
                        // Subpage - check if current path matches exactly
                        isSubmenuActive = location.pathname === basePath;
                      }
                    }

                    let submenuTextColor;
                    if (submenu.isFile || submenu.pdfId) {
                      submenuTextColor =
                        textColor === '#ffffff'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : THEME.COLORS.NEUTRAL_GRAY;
                    } else if (isHomePage) {
                      submenuTextColor = isSubmenuActive
                        ? activeColor
                        : textColor;
                    } else {
                      submenuTextColor = isSubmenuActive
                        ? activeColor
                        : THEME.COLORS.NEUTRAL_GRAY;
                    }

                    if (submenu.isFile || submenu.pdfId) {
                      return (
                        <ListItem
                          key={subIndex}
                          component='a'
                          href={
                            submenu.path ||
                            (submenu.pdfId ? pdfFiles[submenu.pdfId] : '')
                          }
                          target='_blank'
                          rel='noopener noreferrer'
                          sx={{
                            cursor: 'pointer',
                            padding: '2px 8px 2px 0',
                            color: submenuTextColor,
                            '&:hover': {
                              color:
                                textColor === '#ffffff'
                                  ? THEME.COLORS.NEUTRAL_WHITE
                                  : THEME.COLORS.NEUTRAL_BLACK
                            },
                            textDecoration: 'none',
                            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 0
                          }}
                        >
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: '0.4rem',
                              color: activeColor,
                              mr: 1
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT
                            }}
                          >
                            {submenu.title}
                          </Typography>
                        </ListItem>
                      );
                    } else if (submenu.path) {
                      // Handles navigation to a new page or a section on a new page
                      const pathParts = submenu.path.split('#');
                      const basePath = pathParts[0];
                      const sectionId =
                        pathParts.length > 1 ? pathParts[1] : 'hero';
                      return (
                        <ListItem
                          key={subIndex}
                          onClick={() => handleScroll(sectionId, basePath)}
                          sx={{
                            cursor: 'pointer',
                            padding: '2px 8px',
                            color: isSubmenuActive
                              ? activeColor
                              : isHomePage
                                ? textColor
                                : THEME.COLORS.NEUTRAL_GRAY,
                            '&:hover': { color: activeColor },
                            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                            marginBottom: 0
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT
                            }}
                          >
                            {submenu.title}
                          </Typography>
                        </ListItem>
                      );
                    } else {
                      // Handles scrolling to a section on the current page
                      return (
                        <ListItem
                          key={subIndex}
                          onClick={() => handleScroll(submenu.id, item.path)}
                          sx={{
                            cursor: 'pointer',
                            padding: '2px 8px',
                            color:
                              activeSection === submenu.id
                                ? activeColor
                                : isHomePage
                                  ? textColor
                                  : THEME.COLORS.NEUTRAL_GRAY,
                            '&:hover': { color: activeColor },
                            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                            marginBottom: 0
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT
                            }}
                          >
                            {submenu.title}
                          </Typography>
                        </ListItem>
                      );
                    }
                  })}
                </List>
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <ListItem
      onClick={() => handleScroll('hero', item.path)}
      sx={{
        padding: '3px 8px',
        marginLeft: '25px',
        fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT,
        textAlign: 'left',
        color: location.pathname === item.path ? activeColor : textColor,
        '&:hover': {
          color: activeColor,
          cursor: 'pointer'
        },
        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
      }}
    >
      <Typography
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit'
        }}
      >
        {item.title}
      </Typography>
    </ListItem>
  );
};

NavigationMenuItem.propTypes = {
  item: PropTypes.object.isRequired,
  expandedMenu: PropTypes.string,
  handleMenuToggle: PropTypes.func.isRequired,
  handleScroll: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  activeSection: PropTypes.string,
  textColor: PropTypes.string.isRequired,
  activeColor: PropTypes.string.isRequired
};

export default NavigationMenuItem;
